import { uk, type Dict, type DictKey } from './uk';

export type Locale = 'uk' | 'en' | 'pl';
export type { DictKey };

/** Додаткові мови підключаються тут: `en: enDict` (Partial — відсутні ключі беруться з uk). */
const dictionaries: Record<Locale, Partial<Dict>> = {
  uk,
  en: {},
  pl: {},
};

let current: Locale = 'uk';

export function setLocale(l: Locale): void {
  current = l;
  if (typeof document !== 'undefined') document.documentElement.lang = l;
}

export function getLocale(): Locale {
  return current;
}

/** Intl-локаль для форматування чисел і дат. */
export function intlLocale(): string {
  return current === 'uk' ? 'uk-UA' : current === 'pl' ? 'pl-PL' : 'en-GB';
}

export type Vars = Record<string, string | number>;

/**
 * Множина: `{n|день|дні|днів}` → «1 день», «3 дні», «5 днів» (форми one|few|many за Intl.PluralRules).
 * Звичайна підстановка — `{n}`.
 */
export function t(key: DictKey, vars?: Vars): string {
  const raw = dictionaries[current][key] ?? uk[key] ?? key;
  if (!vars) return raw;
  return raw
    .replace(/\{(\w+)\|([^}]+)\}/g, (m, name: string, forms: string) => (name in vars ? plural(Number(vars[name]), forms.split('|')) : m))
    .replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

const rulesCache = new Map<string, Intl.PluralRules>();
/** forms: [one, few, many] (+ other для дробів; якщо немає — many). */
export function plural(n: number, forms: string[]): string {
  const loc = intlLocale();
  let r = rulesCache.get(loc);
  if (!r) rulesCache.set(loc, (r = new Intl.PluralRules(loc)));
  const cat = r.select(n);
  const [one, few = one, many = few, other = many] = forms;
  return `${n} ${cat === 'one' ? one : cat === 'few' ? few : cat === 'many' ? many : other}`;
}

/** Переклад для динамічного ключа (напр. статус із сервера); якщо ключа немає — fallback. */
export function tk(prefix: string, value: string | null | undefined, fallback?: string): string {
  if (value == null || value === '') return fallback ?? '—';
  const key = `${prefix}.${value}` as DictKey;
  if (key in uk) return t(key);
  return fallback ?? value;
}

/** Багатомовне поле з сервера: рядок або {uk, en, ...}. */
export type L10n = string | Partial<Record<string, string>> | null | undefined;

export function loc(v: L10n, fallback = ''): string {
  if (v == null) return fallback;
  if (typeof v === 'string') return v;
  return v[current] ?? v.uk ?? v.en ?? Object.values(v).find((x) => !!x) ?? fallback;
}
